import { User, Mars, Venus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_GENERATIONS = 3;

function NodeCard({ node, role }) {
  if (!node) {
    return (
      <div
        className="bg-cream-2 border border-dashed border-app-border rounded-lg p-3 text-center text-xs text-espresso/50 min-w-[160px]"
        data-testid="pedigree-empty"
      >
        Unknown {role}
      </div>
    );
  }
  const sexColor =
    node.sex === "male"
      ? "bg-blue-50 border-blue-200 text-blue-800"
      : node.sex === "female"
      ? "bg-pink-50 border-pink-200 text-pink-800"
      : "bg-white border-app-border text-espresso";
  const SexIcon = node.sex === "male" ? Mars : node.sex === "female" ? Venus : User;

  return (
    <div
      className={`rounded-xl border ${sexColor} p-3 min-w-[160px] max-w-[220px] shadow-sm`}
      data-testid={`pedigree-node-${node.ring}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        {node.photo_id ? (
          <img
            src={`${BACKEND_URL}/api/photos/${node.photo_id}`}
            alt={node.ring}
            className="w-10 h-10 rounded-lg object-cover border border-white"
          />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-white/60 grid place-items-center">
            <SexIcon className="w-4 h-4 opacity-70" />
          </div>
        )}
        <div className="min-w-0">
          <div className="font-mono font-bold text-sm truncate">{node.ring}</div>
          <div className="text-[10px] uppercase tracking-widest opacity-70">{role}</div>
        </div>
      </div>
      {node.mutation && <div className="text-xs opacity-80 truncate">{node.mutation}</div>}
    </div>
  );
}

// Iterative render: flatten tree into levels [[subject], [father, mother], [ff, fm, mf, mm], ...]
function flatten(root) {
  const levels = [[{ node: root, role: "Subject" }]];
  let current = [{ node: root, role: "Subject" }];
  for (let depth = 0; depth < MAX_GENERATIONS; depth++) {
    const next = [];
    let anyReal = false;
    for (const item of current) {
      const n = item.node;
      const father = n ? n.father : null;
      const mother = n ? n.mother : null;
      next.push({ node: father, role: "Father" });
      next.push({ node: mother, role: "Mother" });
      if (father || mother) anyReal = true;
    }
    if (!anyReal) break;
    levels.push(next);
    current = next;
  }
  return levels;
}

export default function PedigreeTree({ root }) {
  if (!root) return null;
  const levels = flatten(root);
  return (
    <div className="space-y-4 inline-block" data-testid="pedigree-tree">
      {levels.map((row, i) => (
        <div
          key={i}
          className="grid gap-3 justify-items-center"
          style={{ gridTemplateColumns: `repeat(${row.length}, minmax(160px, 1fr))` }}
        >
          {row.map((item, j) => (
            <NodeCard key={`${i}-${j}`} node={item.node} role={item.role} />
          ))}
        </div>
      ))}
    </div>
  );
}
